<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <!-- Header with Status and Actions -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>

              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h1 class="text-2xl font-bold text-gray-900">
                    {{ $t('HeadContract.ProgressClaimsShowLite.title') }}
                  </h1>

                  <template
                    v-if="claim"
                  >
                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShowLite.claimNumber', { claimNumber: claim.claim_number }) }}
                    </h2>

                    <span class="text-gray-400">•</span>

                    <p class="text-lg text-gray-900">
                      <span class="font-medium">Amount Payable (excl. GST) {{ australianCurrency(claim.totalPayable) }}</span>
                    </p>

                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusBadgeClass(claim.status)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getStatusBadgeDotClass(claim.status)"
                      />
                      {{ $t(`Models.Claim.statusTitles.${enumKeyFromValue(ClaimStatus, claim.status)}`) }}
                    </span>
                  </template>
                </div>

                <div
                  v-if="claim"
                  class="flex items-center space-x-3"
                >
                  <p class="text-sm text-gray-600">
                    {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                  </p>

                  <div
                    v-if="showRetention && !isReadOnly && typingRetention"
                    class="flex items-center"
                  >
                    <span class="text-sm font-medium text-gray-900 flex-shrink-0">{{ $t('HeadContract.ProgressClaimsShowLite.retention') }}</span>

                    <div class="w-full">
                      <AppCurrencyInput
                        ref="retentionInput"
                        v-model="retention"
                        class="form-input ml-2 w-3/4"
                        type="text"
                        name="retention"
                        @keyup.esc="typingRetention = false"
                        @blur="updateRetention"
                        @keyup.enter="$event.target.blur()"
                      />
                    </div>
                  </div>

                  <button
                    v-else-if="showRetention && !isReadOnly"
                    type="button"
                    class="flex items-center space-x-2 cursor-pointer btn-icon btn-icon-default"
                    @click="showRetentionInput"
                  >
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShowLite.retention') }} {{ australianCurrency(claim.retention) }}
                    </p>

                    <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>

                    <!-- <p class="text-sm text-gray-900">
                      <span class="font-medium">Amount Payable:</span> {{ australianCurrency(claim.totalPayable) }}
                    </p> -->
                  </button>

                  <div v-else-if="showRetention">
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShowLite.retention') }} {{ australianCurrency(claim.retention) }}
                    </p>
                  </div>

                  <div
                    v-if="claim?.status === ClaimStatus.draft"
                    class="flex items-center"
                  >
                    <label class="flex items-center space-x-1 cursor-pointer">
                      <input
                        v-model="captureClaimAmount"
                        type="checkbox"
                        name="capture_claim_amount"
                        data-test="capture-claim-amount"
                        class="form-checkbox"
                      >

                      <span class="text-sm font-medium text-gray-900">{{ $t('HeadContract.ProgressClaimsShowLite.label.captureClaim') }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div
              v-if="!userStore.isClientManager()"
              class="flex items-center space-x-3"
            >
              <!-- Certify Button for Draft Status -->
              <button
                v-if="claim?.status === ClaimStatus.draft"
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                @click="certifyClaim()"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.certifyClaim') }}</span>
              </button>

              <button
                v-if="claim && claim.status !== ClaimStatus.certified && projectsStore.project?.unarchived"
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="requestApproval(claim.id)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.requestApproval') }}</span>
              </button>

              <!-- Action Buttons for Draft Status -->
              <div
                v-if="claim?.status === ClaimStatus.draft"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click.prevent="showImageModal = true"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.attachFiles') }}</span>
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-alert flex items-center space-x-2"
                  @click.prevent="deleteClaim"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.deleteClaim') }}</span>
                </button>
              </div>

              <!-- Action Buttons for Certified Status -->
              <div
                v-else-if="claim?.status === ClaimStatus.certified"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click.prevent="showImageModal = true"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.attachFiles') }}</span>
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': claimExportAction.is(States.LOADING) }"
                  :disabled="claimExportAction.is(States.LOADING)"
                  @click.prevent="exportClaim"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.exportToPDF') }}</span>
                </button>

                <button
                  v-if="isLatestClaim && userStore.isRole(['company_admin', 'project_admin'])"
                  type="button"
                  class="btn btn--secondary-alert flex items-center space-x-2"
                  :class="{ 'opacity-50': uncertifyClaimAction.is(States.LOADING) }"
                  :disabled="uncertifyClaimAction.is(States.LOADING)"
                  @click.prevent="uncertifyClaim"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShowLite.button.uncertifyClaim') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <ClaimTable
          :project-id="projectId"
          :claim-id="claimId"
          :lump-sum-cost-ids-for-claim-period="lumpSumCostIdsForClaimPeriod"
          :rates-cost-ids-for-claim-period="ratesCostIdsForClaimPeriod"
          :variation-order-cost-ids-for-claim-period="variationOrderCostIdsForClaimPeriod"
        />
      </div>
    </div>

    <ClaimAttachmentsModal
      v-if="showImageModal"
      :claim-id="claimId"
      @close="showImageModal = false;"
    />
  </div>
</template>

<script setup lang="ts">
import ClaimAttachmentsModal from '@/components/HeadContract/ProgressClaimsLite/ClaimAttachmentsModal.vue';
import ClaimTable from '@/components/HeadContract/ProgressClaimsLite/ClaimTable.vue';
import { promptCertificateLayout } from '@/composables/useCertificateLayoutSwal';
import { useRequestClaimApproval } from '@/composables/useRequestClaimApproval';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue, falsey, timeZone } from '@/helpers';
import { australianCurrency } from '@/helpers';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  claimId: string;
}>();

const claimsStore = useClaimsStore();
const router = useRouter();
const projectsStore = useProjectsStore();
const tradesStore = useTradesStore();
const userStore = useUserStore();
const variationOrdersStore = useVariationOrdersStore();
const sharedFundsStore = useSharedFundsStore();
const { requestApproval } = useRequestClaimApproval();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationOrdersAction = useStoreApiAction(variationOrdersStore.fetchApprovedVariationOrders);
const subcontractSummaryAction = useStoreApiAction(claimsStore.fetchSubcontractorClaimsSummary);
const lumpSumCostIdsForClaimPeriod = ref<Id[]>([]);
const ratesCostIdsForClaimPeriod = ref<Id[]>([]);
const variationOrderCostIdsForClaimPeriod = ref<Id[]>([]);

fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
  console.log(error);
});

fetchVariationOrdersAction.request(props.projectId).catch((error) => {
  console.log(error);
});

if(projectsStore.project?.project_group_id) {
  sharedFundsStore.fetchClaimIndex(props.projectId).catch(console.error);
}

watch(() => props.projectId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    router.push({ name: 'ProgressClaimsIndex' });
  }
});

watch(() => props.claimId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    claimsStore.fetchClaim(props.claimId);

    subcontractSummaryAction.request(props.projectId, props.claimId).then((data) => {
      lumpSumCostIdsForClaimPeriod.value = data.lump_sums.map((lumpSumCost) => lumpSumCost.id);
      ratesCostIdsForClaimPeriod.value = data.rates.map((ratesCost) => ratesCost.id);

      variationOrderCostIdsForClaimPeriod.value = data.variation_orders.map((variationOrderCost) =>
        variationOrderCost.id
      );
    });
  }
}, { immediate: true });

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items')
          .where('boq_type', 'direct');
      })
      .with('trade_item_costs')
      .with('variation_orders')
      .with('variation_order_costs')
      .with('shared_fund_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const isLatestClaim = computed(() => {
  if(!claim.value) return false;

  const latestCertifiedClaim = claimsStore.models
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', claim.value.boq_type)
    .where('status', ClaimStatus.certified)
    .orderBy('claim_number', 'desc')
    .first();

  return falsey(latestCertifiedClaim) || latestCertifiedClaim.id === claim.value.id;
});

const certifyClaimAction = useStoreApiAction(claimsStore.certifyLite);
const uncertifyClaimAction = useStoreApiAction(claimsStore.uncertify);

const certifyClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Certify Claim?',
    text: 'Are you sure you want to certify this claim? You will not be able to adjust any amounts after certifying.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      certifyClaimAction.request(props.claimId, { submitted_at_time_zone: timeZone }).then(() => {
        Swal.fire({
          title: 'Claim Certified',
          icon: 'success',
        });
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            title: 'Failed to certify claim',
            text: error.data.message,
            icon: 'error',
          });
        } else {
          Swal.fire({
            title: 'Failed to certify claim',
            icon: 'error',
          });
        }
      });
      // claimsStore.sendClaim(claim.value.id, { status: result.value       });
    }
  });
};

const uncertifyClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Uncertify Claim?',
    text: 'Are you sure you want to uncertify this claim? This will allow the costs and forecasts to be altered.',

    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      uncertifyClaimAction.request(props.claimId).then(() => {
        Swal.fire({
          title: 'Claim Uncertified',
          icon: 'success',
        });
      }).catch((error) => {
        Swal.fire({
          title: 'Failed to uncertify claim',
          icon: 'error',
        });
      });
    }
  });
};

const retention = ref(0);
const typingRetention = ref(false);
const updateClaimAction = useStoreApiAction(claimsStore.updateClaim);
const captureClaimAmount = ref(false);

watch(claim, (newValue) => {
  if(newValue) {
    captureClaimAmount.value = newValue.capture_claim_amount;
    retention.value = newValue.retention;
  }
}, { immediate: true });

const retentionInput = useTemplateRef('retentionInput');

const showRetentionInput = () => {
  typingRetention.value = true;

  nextTick(() => {
    retentionInput.value?.focusInput();
  });
};

const updateRetention = () => {
  if(!typingRetention.value) {
    retention.value = claim.value.retention;

    return;
  }

  if(falsey(retention.value)) {
    typingRetention.value = false;

    retention.value = claim.value.retention;

    return;
  }

  updateClaimAction.request(claim.value.id, { retention: retention.value }).then(() => {
    typingRetention.value = false;
  }).catch((error) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to update retention',
      text: error.data?.message,
    });

    typingRetention.value = true;
  });
};

const deleteClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Claim?',
    text: 'Are you sure you want to delete this claim? This cannot be undone.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      claimsStore.deleteClaim(claim.value.id).then(() => {
        router.push({ name: 'ProgressClaimsIndex' });

        Swal.fire({
          icon: 'success',
          title: 'Claim deleted.',
        });
      });
    }
  });
};

const claimExportAction = useStoreApiAction(claimsStore.export);

const exportClaim = async () => {
  let applySignature = undefined;
  let depth = undefined;
  const compact = await promptCertificateLayout();

  if(compact === undefined) {
    return;
  }

  const result = await Swal.fire({
    title: 'Select Depth to Show on Certificate',
    input: 'number',
    inputPlaceholder: 'Leave blank to show all levels',
    inputLabel: 'Select 0 to show top level trade items, 1 for second level, etc.',
    showCancelButton: true,
    confirmButtonText: 'Export',
  });

  if(!result.isConfirmed) {
    return;
  }

  depth = result.value === '' ? undefined : parseInt(result.value);

  if(userStore.userSignature && userStore.isRole(['company_admin'], false)) {
    const result = await Swal.fire({
      title: 'Sign Claim Export?',
      input: 'checkbox',
      inputPlaceholder: `
        Add my signature to the claim export
      `,
      showCancelButton: true,
      confirmButtonText: 'Export',
    });

    if(result.value === 1) {
      applySignature = true;
    }

    if(result.isDenied || result.isDismissed) {
      return;
    }
  }

  claimExportAction.request(claim.value.id, { apply_signature: applySignature, depth, compact }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Exported.',
      text: 'Claim is being exported to PDF and will be sent to your email when it is ready.',
    });
  }).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export.',
      });
    }
  });
};

const saveCaptureClaimAmount = () => {
  updateClaimAction.request(claim.value.id, { capture_claim_amount: captureClaimAmount.value }).catch((error) => {
    Swal.fire({
      icon: 'error',
      title: 'Something went Wrong.',
      text: 'Failed to Update Claim',
    });
  });
};

watch(captureClaimAmount, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    if(newValue === false) {
      Swal.fire({
        icon: 'warning',
        title: 'Capture Certified Cost Only?',
        text:
          'Are you sure you want to only capture the certified costs? This will remove all currently filled claim amounts.',

        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonColor: 'green',
        confirmButtonText: 'Yes',
      }).then((result) => {
        if(result.isConfirmed) {
          saveCaptureClaimAmount();
        } else {
          captureClaimAmount.value = true;
        }
      });
    } else {
      saveCaptureClaimAmount();
    }
  }
});

const showImageModal = ref(false);

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    default:
      return 'bg-red-400';
  }
};

const isReadOnly = userStore.isClientManager();

const showRetention = computed(() => {
  return projectsStore.project?.head_contract_settings?.performance_security === 'retention';
});
</script>

<style scoped></style>
